/**
 * Utility functions for parsing SQL queries, with support for PostgreSQL dollar-quoted strings
 */
import type * as monaco from 'monaco-editor';

export interface QueryBlock {
  query: string
  startLine: number
  endLine: number
  startColumn: number
  endColumn: number
}

/**
 * Parse Redis commands separated by newlines
 * Redis commands are line-based - each line is a separate command
 * Empty lines and lines starting with # (comments) are ignored
 */
function parseRedisCommands(text: string): QueryBlock[] {
  const queries: QueryBlock[] = []
  const lines = text.split('\n')

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? ''
    const trimmedLine = line.trim()

    // Skip empty lines and comments (lines starting with #)
    if (!trimmedLine || trimmedLine.startsWith('#')) {
      continue
    }

    // Each non-empty, non-comment line is a Redis command
    queries.push({
      query: trimmedLine,
      // 1-based line numbers
      startLine: lineIndex + 1,
      endLine: lineIndex + 1,
      startColumn: 1,
      endColumn: line.length + 1
    })
  }

  return queries
}

/**
 * Parse MongoDB commands with proper handling of JavaScript syntax.
 * MongoDB commands are JavaScript expressions, so semicolons inside braces/brackets/parens
 * should NOT be treated as statement separators. Only semicolons at nesting depth 0
 * are valid statement boundaries.
 */
function parseMongoDBCommands(text: string): QueryBlock[] {
  const queries: QueryBlock[] = []
  const lines = text.split('\n')
  let currentQuery = ''
  let queryStartLine = 1
  let actualQueryStartLine = 1
  let queryStartColumn = 1
  let hasFoundQueryContent = false

  // Nesting depth for {}, [], ()
  let nestingDepth = 0
  let inString = false
  let stringChar = ''
  let inLineComment = false
  let inBlockComment = false

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? ''

    // Reset single-line comment at start of each new line
    inLineComment = false

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex] ?? ''
      const nextChar = line[charIndex + 1]

      // Track the first non-whitespace, non-comment character as the actual query start
      if (!hasFoundQueryContent && char.trim() && !inLineComment && !inBlockComment) {
        actualQueryStartLine = lineIndex + 1
        hasFoundQueryContent = true
      }

      // Handle block comment end
      if (inBlockComment) {
        currentQuery += char
        if (char === '*' && nextChar === '/') {
          inBlockComment = false
          currentQuery += '/'
          charIndex++
        }
        continue
      }

      // Skip line comments
      if (inLineComment) {
        currentQuery += char
        continue
      }

      // Handle string literals
      if (inString) {
        currentQuery += char
        if (char === stringChar) {
          const prevChar = charIndex > 0 ? line[charIndex - 1] : ''
          if (prevChar !== '\\') {
            inString = false
            stringChar = ''
          }
        }
        continue
      }

      // Detect comment starts
      if (char === '/' && nextChar === '/') {
        inLineComment = true
        currentQuery += char
        continue
      }
      if (char === '/' && nextChar === '*') {
        inBlockComment = true
        currentQuery += char
        continue
      }

      // Detect string starts
      if (char === '"' || char === "'" || char === '`') {
        inString = true
        stringChar = char
        currentQuery += char
        continue
      }

      // Track nesting depth
      if (char === '{' || char === '[' || char === '(') {
        nestingDepth++
      } else if (char === '}' || char === ']' || char === ')') {
        nestingDepth = Math.max(0, nestingDepth - 1)
      }

      currentQuery += char

      // Split on semicolons ONLY at nesting depth 0
      if (char === ';' && nestingDepth === 0 && hasFoundQueryContent) {
        const trimmedQuery = currentQuery.trim()
        if (trimmedQuery && trimmedQuery !== ';') {
          queries.push({
            query: trimmedQuery,
            startLine: actualQueryStartLine,
            endLine: lineIndex + 1,
            startColumn: queryStartColumn,
            endColumn: charIndex + 2
          })
        }
        currentQuery = ''
        queryStartLine = lineIndex + 1
        queryStartColumn = charIndex + 2
        hasFoundQueryContent = false
        actualQueryStartLine = lineIndex + 1
      }
    }

    // Add newline except for last line
    if (lineIndex < lines.length - 1) {
      currentQuery += '\n'
    }
  }

  // Add the last query if it doesn't end with semicolon
  const trimmedQuery = currentQuery.trim()
  if (trimmedQuery) {
    if (!hasFoundQueryContent) {
      const queryLines = currentQuery.split('\n')
      for (const [i, queryLine] of queryLines.entries()) {
        if (queryLine.trim()) {
          actualQueryStartLine = queryStartLine + i
          break
        }
      }
    }
    const lastLine = lines[lines.length - 1] ?? ''
    queries.push({
      query: trimmedQuery,
      startLine: actualQueryStartLine,
      endLine: lines.length,
      startColumn: queryStartColumn,
      endColumn: lastLine.length + 1
    })
  }

  return queries
}

/**
 * Parse SQL queries separated by semicolon (or newline for Redis), with proper support for PostgreSQL dollar-quoted strings
 * @param sql - The SQL/command string to parse
 * @param databaseType - Optional database type (e.g., 'redis', 'mysql', 'postgresql'). Redis uses newline as separator.
 */
export function parseQueries(sql: string, databaseType?: string): QueryBlock[] {
  if (!sql.trim()) return []

  // For Redis and Memcached, use newline as the command separator instead of semicolon
  const isRedis = databaseType?.toLowerCase() === 'redis'
  const isMemcached = databaseType?.toLowerCase() === 'memcached'
  const isMongoDB = databaseType?.toLowerCase() === 'mongodb'

  if (isRedis || isMemcached) {
    // Reuse the same command parsing logic for both
    return parseRedisCommands(sql)
  }

  // MongoDB commands are JavaScript - use a JS-aware parser that respects brace nesting
  if (isMongoDB) {
    return parseMongoDBCommands(sql)
  }

  const queries: QueryBlock[] = []
  const lines = sql.split('\n')
  let currentQuery = ''
  let queryStartLine = 1
  // Track where the actual query content starts
  let actualQueryStartLine = 1
  let queryStartColumn = 1
  let inString = false
  let stringChar = ''
  let inComment = false
  // '--' or '/*'
  let commentType = ''
  let inDollarQuote = false
  let dollarQuoteTag = ''
  // Track if we've found actual query content
  let hasFoundQueryContent = false

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? ''

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex] ?? ''
      const nextChar = line[charIndex + 1]

      // Track the first non-whitespace, non-comment character as the actual query start
      if (!hasFoundQueryContent && char.trim() && !inComment && !inDollarQuote) {
        actualQueryStartLine = lineIndex + 1
        hasFoundQueryContent = true
      }

      // Handle PostgreSQL dollar-quoted strings (e.g., $$, $tag$)
      if (!inString && !inComment && char === '$') {
        if (!inDollarQuote) {
          // Potential start of dollar quote - find the tag
          let tagEnd = charIndex + 1
          while (tagEnd < line.length && (line[tagEnd] ?? '') !== '$') {
            const tagChar = line[tagEnd] ?? ''
            // Valid tag characters: letters, digits, underscores
            if (!/[a-zA-Z0-9_]/.test(tagChar)) {
              break
            }
            tagEnd++
          }

          if (tagEnd < line.length && (line[tagEnd] ?? '') === '$') {
            // Found complete dollar quote tag
            dollarQuoteTag = line.slice(charIndex, tagEnd + 1)
            inDollarQuote = true
            // Add the full opening tag to currentQuery
            currentQuery += line.slice(charIndex, tagEnd + 1)
            // Skip to the end of the tag
            charIndex = tagEnd
            continue
          }
        } else {
          // Check if this is the end of the current dollar quote
          const remainingLine = line.slice(charIndex)
          if (remainingLine.startsWith(dollarQuoteTag)) {
            inDollarQuote = false
            const tagLength = dollarQuoteTag.length
            // Add the closing tag to currentQuery
            currentQuery += line.slice(charIndex, charIndex + tagLength)
            dollarQuoteTag = ''
            // Skip the tag (minus 1 because loop will increment)
            charIndex += tagLength - 1
            // Skip the rest of this iteration to avoid reprocessing
            continue
          }
        }
      }

      // Handle regular string literals (only if not in dollar quote)
      if (!inDollarQuote && !inComment && (char === '"' || char === "'" || char === '`')) {
        if (!inString) {
          inString = true
          stringChar = char
        } else if (char === stringChar) {
          // Check for backslash escape (MySQL default behavior)
          const prevChar = charIndex > 0 ? line[charIndex - 1] : ''
          if (prevChar !== '\\') {
            inString = false
            stringChar = ''
          }
        }
      }

      // Handle comments (only if not in string or dollar quote)
      if (!inString && !inDollarQuote) {
        if (char === '-' && nextChar === '-') {
          inComment = true
          commentType = '--'
        } else if (char === '/' && nextChar === '*') {
          inComment = true
          commentType = '/*'
        } else if (inComment && commentType === '/*' && char === '*' && nextChar === '/') {
          inComment = false
          commentType = ''
          // Skip next character
          charIndex++
          continue
        }
      }

      currentQuery += char

      // Check for semicolon (end of query) - only if not in string, comment, or dollar quote
      if (!inString && !inComment && !inDollarQuote && char === ';' && hasFoundQueryContent) {
        const trimmedQuery = currentQuery.trim()
        if (trimmedQuery && trimmedQuery !== ';') {
          const queryBlock = {
            query: trimmedQuery,
            // Use the actual query start line
            startLine: actualQueryStartLine,
            endLine: lineIndex + 1,
            startColumn: queryStartColumn,
            endColumn: charIndex + 2
          }
          queries.push(queryBlock)
        }

        // Reset for next query
        currentQuery = ''
        queryStartLine = lineIndex + 1
        queryStartColumn = charIndex + 2
        // Reset for next query
        hasFoundQueryContent = false
        // Reset actual start line for next query
        actualQueryStartLine = lineIndex + 1
      }
    }

    // Reset comment flag at end of line only for single-line comments (--)
    if (inComment && commentType === '--') {
      inComment = false
      commentType = ''
    }
    
    // Add newline character except for the last line
    if (lineIndex < lines.length - 1) {
      currentQuery += '\n'
    }
  }
  
  // Add the last query if it doesn't end with semicolon
  const trimmedQuery = currentQuery.trim()
  if (trimmedQuery) {
    // Find the actual start line for the last query
    if (!hasFoundQueryContent) {
      // Find the first non-empty line in the remaining query
      const queryLines = currentQuery.split('\n')
      let foundStartLine = false
      for (const [i, queryLine] of queryLines.entries()) {
        if (queryLine.trim()) {
          // Calculate the actual line number in the original text
          actualQueryStartLine = queryStartLine + i
          foundStartLine = true
          break
        }
      }
      // If no content found, use the current queryStartLine
      if (!foundStartLine) {
        actualQueryStartLine = queryStartLine
      }
    }

    const lastLine = lines[lines.length - 1] ?? ''
    const queryBlock = {
      query: trimmedQuery,
      startLine: actualQueryStartLine,
      endLine: lines.length,
      startColumn: queryStartColumn,
      endColumn: lastLine.length + 1
    }
    queries.push(queryBlock)
  }
  
  return queries
}

/**
 * Find the query block that contains the given line number.
 * Returns null if the line is not inside any query block.
 * @param sql - The full SQL text
 * @param lineNumber - 1-based line number
 * @param databaseType - Optional database type
 */
export function getQueryBlockAtLine(sql: string, lineNumber: number, databaseType?: string): QueryBlock | null {
  const blocks = parseQueries(sql, databaseType)
  for (const block of blocks) {
    if (lineNumber >= block.startLine && lineNumber <= block.endLine) {
      return block
    }
  }
  return null
}

/**
 * Create folding ranges for SQL queries in Monaco editor, supporting PostgreSQL dollar-quoted strings
 */
export function createSqlFoldingRanges(text: string, monacoInstance: typeof monaco): monaco.languages.FoldingRange[] {
  const lines = text.split('\n')
  const foldingRanges: monaco.languages.FoldingRange[] = []
  
  let currentQueryStart = 0
  let inString = false
  let stringChar = ''
  let inComment = false
  // '--' or '/*'
  let commentType = ''
  let inDollarQuote = false
  let dollarQuoteTag = ''
  let hasFoundQueryContent = false

  for (const [lineIndex, line] of lines.entries()) {

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex] ?? ''
      const nextChar = line[charIndex + 1]

      // Track if we've found actual query content (non-whitespace, non-comment)
      if (!hasFoundQueryContent && char.trim() && !inComment && !inDollarQuote) {
        currentQueryStart = lineIndex
        hasFoundQueryContent = true
      }

      // Handle PostgreSQL dollar-quoted strings
      if (!inString && !inComment && char === '$') {
        if (!inDollarQuote) {
          // Potential start of dollar quote - find the tag
          let tagEnd = charIndex + 1
          while (tagEnd < line.length && (line[tagEnd] ?? '') !== '$') {
            const tagChar = line[tagEnd] ?? ''
            // Valid tag characters: letters, digits, underscores
            if (!/[a-zA-Z0-9_]/.test(tagChar)) {
              break
            }
            tagEnd++
          }

          if (tagEnd < line.length && (line[tagEnd] ?? '') === '$') {
            // Found complete dollar quote tag
            dollarQuoteTag = line.slice(charIndex, tagEnd + 1)
            inDollarQuote = true
            // Skip to the end of the tag
            charIndex = tagEnd
          }
        } else {
          // Check if this is the end of the current dollar quote
          const remainingLine = line.slice(charIndex)
          if (remainingLine.startsWith(dollarQuoteTag)) {
            inDollarQuote = false
            const tagLength = dollarQuoteTag.length
            dollarQuoteTag = ''
            // Skip the tag (minus 1 because loop will increment)
            charIndex += tagLength - 1
            // Skip the rest of this iteration to avoid reprocessing
            continue
          }
        }
      }

      // Handle regular string literals (only if not in dollar quote)
      if (!inDollarQuote && !inComment && (char === '"' || char === "'" || char === '`')) {
        if (!inString) {
          inString = true
          stringChar = char
        } else if (char === stringChar) {
          // Check for backslash escape (MySQL default behavior)
          const prevChar = charIndex > 0 ? line[charIndex - 1] : ''
          if (prevChar !== '\\') {
            inString = false
            stringChar = ''
          }
        }
      }

      // Handle comments (only if not in string or dollar quote)
      if (!inString && !inDollarQuote) {
        if (char === '-' && nextChar === '-') {
          inComment = true
          commentType = '--'
        } else if (char === '/' && nextChar === '*') {
          inComment = true
          commentType = '/*'
        } else if (inComment && commentType === '/*' && char === '*' && nextChar === '/') {
          inComment = false
          commentType = ''
          // Skip next character
          charIndex++
          continue
        }
      }

      // Check for semicolon (end of query) - only if not in string, comment, or dollar quote
      if (!inString && !inComment && !inDollarQuote && char === ';' && hasFoundQueryContent) {
        // Create folding range if query spans multiple lines
        if (lineIndex > currentQueryStart) {
          foldingRanges.push({
            // Monaco uses 1-based line numbers
            start: currentQueryStart + 1,
            end: lineIndex + 1,
            kind: monacoInstance.languages.FoldingRangeKind.Region
          })
        }

        // Reset for next query
        hasFoundQueryContent = false
        currentQueryStart = lineIndex + 1
      }
    }

    // Reset comment flag at end of line only for single-line comments (--)
    if (inComment && commentType === '--') {
      inComment = false
      commentType = ''
    }
  }
  
  // Handle the last query if it doesn't end with semicolon and spans multiple lines
  if (hasFoundQueryContent && lines.length - 1 > currentQueryStart) {
    foldingRanges.push({
      start: currentQueryStart + 1,
      end: lines.length,
      kind: monacoInstance.languages.FoldingRangeKind.Region
    })
  }
  
  return foldingRanges
}

/**
 * Strip SQL comments from a query while preserving string literals and PostgreSQL dollar-quoted strings
 */
export function stripComments(sql: string): string {
  if (!sql.trim()) return sql

  let result = ''
  let inString = false
  let stringChar = ''
  let inComment = false
  // '--' or '/*'
  let commentType = ''
  let inDollarQuote = false
  let dollarQuoteTag = ''
  const lines = sql.split('\n')

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? ''

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      const char = line[charIndex] ?? ''
      const nextChar = line[charIndex + 1]

      // Handle PostgreSQL dollar-quoted strings (e.g., $$, $tag$)
      if (!inString && !inComment && char === '$') {
        if (!inDollarQuote) {
          // Potential start of dollar quote - find the tag
          let tagEnd = charIndex + 1
          while (tagEnd < line.length && (line[tagEnd] ?? '') !== '$') {
            const tagChar = line[tagEnd] ?? ''
            // Valid tag characters: letters, digits, underscores
            if (!/[a-zA-Z0-9_]/.test(tagChar)) {
              break
            }
            tagEnd++
          }

          if (tagEnd < line.length && (line[tagEnd] ?? '') === '$') {
            // Found complete dollar quote tag
            dollarQuoteTag = line.slice(charIndex, tagEnd + 1)
            inDollarQuote = true
            result += line.slice(charIndex, tagEnd + 1)
            // Skip to the end of the tag
            charIndex = tagEnd
            continue
          }
        } else {
          // Check if this is the end of the current dollar quote
          const remainingLine = line.slice(charIndex)
          if (remainingLine.startsWith(dollarQuoteTag)) {
            inDollarQuote = false
            const tagLength = dollarQuoteTag.length
            result += line.slice(charIndex, charIndex + tagLength)
            dollarQuoteTag = ''
            // Skip the tag (minus 1 because loop will increment)
            charIndex += tagLength - 1
            continue
          }
        }
      }

      // Handle regular string literals (only if not in dollar quote)
      if (!inDollarQuote && !inComment && (char === '"' || char === "'" || char === '`')) {
        if (!inString) {
          inString = true
          stringChar = char
        } else if (char === stringChar) {
          // Check for backslash escape (MySQL default behavior)
          const prevChar = charIndex > 0 ? line[charIndex - 1] : ''
          if (prevChar !== '\\') {
            inString = false
            stringChar = ''
          }
        }
        result += char
        continue
      }

      // Handle comments (only if not in string or dollar quote)
      if (!inString && !inDollarQuote) {
        if (char === '-' && nextChar === '-') {
          inComment = true
          commentType = '--'
          // Skip the next character
          charIndex++
          continue
        } else if (char === '/' && nextChar === '*') {
          inComment = true
          commentType = '/*'
          // Skip the next character
          charIndex++
          continue
        } else if (inComment && commentType === '/*' && char === '*' && nextChar === '/') {
          inComment = false
          commentType = ''
          // Skip next character
          charIndex++
          continue
        }
      }

      // Add character to result only if not in comment
      if (!inComment) {
        result += char
      }
    }
    
    // Reset comment flag at end of line for single-line comments
    if (inComment && commentType === '--') {
      inComment = false
      commentType = ''
    }
    
    // Add newline character except for the last line (only if not in comment)
    if (lineIndex < lines.length - 1 && !inComment) {
      result += '\n'
    }
  }
  
  return result.trim()
}